/**
 * Hint quality — the per-question tab.
 *
 * One row per question hints have been shown on. The reviewer's questions, in
 * column order: how much help does this question need, how deep did it have to
 * go, and did students find it useful.
 *
 * Read-only. Row click opens the question's drill-down.
 *
 * See docs/FeatureDesigns/HintSystem/hint-quality-review.md §7.
 */

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../api';
import { LatexText } from '../latex';
import {
  DataTable, SearchFilterBar, Pagination,
  type ColumnDef, type FilterDef, type SortState,
} from '../components';
import { useSubjects, useSubjectLabel } from '../reference-data';
import { createHintQualityApi, sinceDateFromRange } from './hintQualityApi';
import { ReviewCell } from './ReviewCell';
import {
  HINT_LEVELS, HINT_CATEGORY_LABELS, HINT_REVIEW_FILTER_OPTIONS,
  type HintQualityRow, type HintQualityAssignmentOption,
} from './types';
import './HintQuality.css';

const PAGE_SIZE = 20;

const DATE_RANGES = [
  { value: '30', label: 'Last 30 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '', label: 'All time' },
];

interface Filters {
  dateRange: string;
  subject: string;
  search: string;
  hasNegative: string;
  questionSetId: string;
  review: string;
}

const DEFAULT_FILTERS: Filters = {
  dateRange: '30', subject: '', search: '', hasNegative: '', questionSetId: '', review: '',
};

/** Assignment options carry the date they were given — the title alone rarely
 *  says which one you mean. */
function formatAssignmentLabel(a: { title: string; date?: string }): string {
  if (!a.date) return a.title;
  const d = new Date(a.date);
  if (Number.isNaN(d.getTime())) return a.title;
  return `${a.title} (${d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })})`;
}

export interface HintQualityQuestionsTabProps {
  /** Opens the drill-down for a question. The host app owns routing. */
  onQuestionClick: (questionId: string) => void;
}

/** Sessions bucketed by deepest level, as a compact left-to-right bar. Reads as
 *  "how far did we have to lead them" — weight toward the right is the signal. */
function LevelBar({ row }: { row: HintQualityRow }) {
  const total = HINT_LEVELS.reduce((n, l) => n + (row.maxLevelReached[l.value] || 0), 0);
  if (!total) return <span className="hintq-muted">—</span>;
  return (
    <div className="hintq-levelbar" title={HINT_LEVELS
      .map(l => `${l.label}: ${row.maxLevelReached[l.value] || 0}`).join('\n')}>
      {HINT_LEVELS.map(l => {
        const n = row.maxLevelReached[l.value] || 0;
        if (!n) return null;
        return (
          <span
            key={l.value}
            className={`hintq-levelseg hintq-level--${l.value}`}
            style={{ flexGrow: n }}
          >
            {n}
          </span>
        );
      })}
    </div>
  );
}

/** Ratings as up / down / unrated. Unrated is shown deliberately: 2 👎 out of 3
 *  rated hints and 2 out of 200 are very different facts. */
function RatingCell({ row }: { row: HintQualityRow }) {
  if (!row.thumbsUp && !row.thumbsDown) {
    return <span className="hintq-muted" title={`${row.unrated} unrated`}>—</span>;
  }
  return (
    <div className="hintq-ratings" title={`${row.unrated} unrated`}>
      {row.thumbsUp > 0 && <span className="hintq-rating hintq-rating--up">👍 {row.thumbsUp}</span>}
      {row.thumbsDown > 0 && <span className="hintq-rating hintq-rating--down">👎 {row.thumbsDown}</span>}
    </div>
  );
}

export function HintQualityQuestionsTab({ onQuestionClick }: HintQualityQuestionsTabProps) {
  const api = useApi();
  const hintQualityApi = useMemo(() => createHintQualityApi(api), [api]);
  const { subjects } = useSubjects();
  // Resolves a subject's display name from the catalog — the internal name
  // (`maths`, `physics`) must never reach the UI.
  const subjectLabel = useSubjectLabel();

  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<SortState>({ sortBy: 'hintsShown', sortOrder: 'desc' });
  // Hold the last non-empty option list: the server computes it before applying
  // the assignment filter, but an unrelated filter can still empty the response,
  // and the dropdown shouldn't lose its options underneath the user.
  const [assignmentOptions, setAssignmentOptions] = useState<HintQualityAssignmentOption[]>([]);

  const filterDefs = useMemo<FilterDef[]>(() => [
    { key: 'dateRange', kind: 'select', placeholder: 'Date range', options: DATE_RANGES, required: true },
    {
      key: 'subject',
      kind: 'select',
      placeholder: 'All subjects',
      // Always the catalog's displayName — never the internal subject name.
      options: subjects.map(s => ({ value: s.name, label: s.displayName })),
    },
    // Only assignments hints were actually taken in — the server derives the
    // list from the hint data, so no option here can return an empty table.
    {
      key: 'questionSetId',
      kind: 'select',
      placeholder: 'All assignments',
      options: assignmentOptions.map(a => ({ value: a.id, label: formatAssignmentLabel(a) })),
    },
    {
      key: 'review',
      kind: 'select',
      placeholder: 'Any review state',
      options: HINT_REVIEW_FILTER_OPTIONS,
    },
    {
      key: 'hasNegative',
      kind: 'select',
      placeholder: 'Any feedback',
      options: [{ value: 'true', label: 'Has 👎 only' }],
    },
  ], [subjects, assignmentOptions]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['hint-quality-rollup', page, filters, sort],
    queryFn: () => hintQualityApi.getRollup({
      page,
      pageSize: PAGE_SIZE,
      sinceDate: sinceDateFromRange(filters.dateRange),
      subject: filters.subject || undefined,
      search: filters.search || undefined,
      hasNegative: filters.hasNegative === 'true',
      questionSetId: filters.questionSetId || undefined,
      review: filters.review || undefined,
      sortBy: sort.sortBy,
      sortDir: sort.sortOrder,
    }),
  });

  const rows = data?.items || [];
  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;
  useEffect(() => {
    if (data?.assignments?.length) setAssignmentOptions(data.assignments);
  }, [data?.assignments]);

  const columns = useMemo<ColumnDef<HintQualityRow>[]>(() => [
    {
      key: 'question',
      header: 'Question',
      render: r => (
        <div className="hintq-qcell">
          <LatexText text={r.questionText || '(no text)'} truncate={140} />
          <span className="hintq-qmeta">
            {r.subject ? subjectLabel(r.subject) : '—'}{r.topic ? ` · ${r.topic}` : ''}
          </span>
        </div>
      ),
    },
    {
      key: 'hintsShown',
      header: 'Hints',
      sortable: true,
      className: 'hintq-num',
      render: r => (
        <div className="hintq-stack">
          <strong>{r.hintsShown}</strong>
          {r.hintsPrewritten > 0 && (
            <span className="hintq-sub" title="AI-generated / ready-made">
              {r.hintsLlm} AI · {r.hintsPrewritten} ready
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'sessions',
      header: 'Attempts',
      sortable: true,
      className: 'hintq-num',
      render: r => (
        <div className="hintq-stack">
          <strong>{r.sessions}</strong>
          <span className="hintq-sub">{r.students} student{r.students === 1 ? '' : 's'}</span>
        </div>
      ),
    },
    {
      key: 'avgHintsPerSession',
      header: 'Avg / attempt',
      sortable: true,
      className: 'hintq-num',
      render: r => r.avgHintsPerSession.toFixed(1),
    },
    {
      key: 'level',
      header: 'Depth reached',
      width: '160px',
      render: r => <LevelBar row={r} />,
    },
    {
      key: 'categories',
      header: 'Asked for',
      render: r => {
        const top = Object.entries(r.categories)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 2);
        if (!top.length) return <span className="hintq-muted">—</span>;
        return (
          <div className="hintq-chips">
            {top.map(([cat, n]) => (
              <span key={cat} className="hintq-chip">
                {HINT_CATEGORY_LABELS[cat as keyof typeof HINT_CATEGORY_LABELS] || cat} · {n}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'reviewedPoor',
      header: 'Review',
      sortable: true,
      render: r => <ReviewCell row={r} />,
    },
    {
      key: 'thumbsDown',
      header: 'Student',
      sortable: true,
      className: 'hintq-num',
      render: r => <RatingCell row={r} />,
    },
  ], [subjectLabel]);

  if (error) {
    return <div className="hintq-error">Couldn't load hint data: {(error as Error).message}</div>;
  }

  return (
    <>
      <SearchFilterBar
        filters={filterDefs}
        values={filters as unknown as Record<string, string>}
        onChange={v => { setFilters(f => ({ ...f, ...(v as Partial<Filters>) })); setPage(1); }}
        searchPlaceholder="Search question text or topic…"
        loading={isFetching}
        hideFetch
        trailingActions={totalPages > 1 ? (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={data?.total}
            onPageChange={setPage}
            disabled={isFetching}
          />
        ) : undefined}
      />

      <DataTable
        columns={columns}
        data={rows}
        rowKey={r => r.questionId}
        onRowClick={r => onQuestionClick(r.questionId)}
        sort={sort}
        onSortChange={s => { setSort(s); setPage(1); }}
        loading={isLoading}
        emptyMessage={
          filters.search || filters.subject || filters.questionSetId || filters.review
            || filters.hasNegative === 'true'
            ? 'No questions match these filters.'
            : 'No hints have been shown yet in this period.'
        }
      />
    </>
  );
}
